/**
 * The open visits on the floor, and the bill each one is running.
 *
 * This is what replaced the party→guest picker. A timed session, a package sale
 * and a kids-area check-in all used to attach to a `PartyParticipant`, so every
 * one of them started by asking "which party, then which guest inside it". Party
 * is gone and a bill now reaches its table through the table's open `Session`,
 * so the same three actions ask one question instead of two: **which visit** —
 * and the visit's master check is what the charge lands on.
 *
 * The question is not cosmetically different, it is the one the API asks now.
 * `StartSessionDto` takes a `tableId` and a `checkId`; `KidsCheckInDto` takes the
 * same pair; `SellPackageDto` takes a `checkId` and nothing else. All three are
 * answered by picking a visit off this list.
 */
import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';

/** A row of `GET /sessions`, narrowed to what a picker needs. */
export interface VisitRow {
  id: string;
  status: string;
  guestCount: number;
  openedAt: string;
  tableId: string | null;
  table?: {
    id: string;
    number: string;
    name?: string | null;
    area?: { id: string; name: string; floor?: { branchId: string } | null } | null;
  } | null;
}

/** The bill a visit is running, as `GET /checks?sessionId=&unsettled=true` answers it. */
export interface VisitCheck {
  id: string;
  number: string;
  status: string;
  currency: string;
}

/** The branch a visit is seated in, which is the branch its money is in. */
export function branchOfVisit(visit: VisitRow | null): string | null {
  return visit?.table?.area?.floor?.branchId ?? null;
}

/**
 * Open visits, newest first.
 *
 * A visit with no table is included rather than filtered out: a resource QR
 * opens one, and it is still a bill somebody may want to put a package on. The
 * label falls back to the opening time, because a table number is the only
 * name a visit ever has and a tableless one does not have that.
 */
export function useOpenVisits(enabled: boolean, branchId?: string | null) {
  const [visits, setVisits] = useState<VisitRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    api
      .get<{ data?: VisitRow[] }>('/sessions', {
        params: {
          status: 'OPEN',
          limit: 100,
          sortBy: 'openedAt',
          sortOrder: 'desc',
          ...(branchId ? { branchId } : {}),
        },
      })
      .then(({ data }) => {
        if (!cancelled) setVisits(data?.data ?? []);
      })
      // Without `sessions.view` the picker is simply empty, not broken.
      .catch(() => {
        if (!cancelled) setVisits([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, branchId]);

  return { visits, loading };
}

/**
 * The visit's master check — the bill a charge routed through this visit lands
 * on, and the grant holder a package would be sold against.
 *
 * Read rather than derived: a visit runs an ordered SEQUENCE of bills, so
 * "the bill of this visit" has no single answer from a row alone — it is
 * whichever one is still unsettled, and the server decides that.
 */
export function useVisitCheck(sessionId: string | null) {
  const [check, setCheck] = useState<VisitCheck | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setCheck(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .get<{ data?: VisitCheck[] }>('/checks', {
        /**
         * The bill this visit is RUNNING, which is not the same as its first.
         *
         * This asked for `isMaster: true` and happened to be right, because a
         * settled table that ordered again grew a second master and the newest
         * one came back. A visit now runs an ordered sequence with exactly one
         * first bill, so the same question returns bill 1 — closed, once the
         * table has paid and carried on. Selling a package against a closed
         * bill is a 409 the operator cannot act on.
         *
         * `unsettled` because "still running" is two statuses and `status` is
         * one value; `asc` because charges route to the OLDEST live bill, so
         * this has to name the bill the till is actually adding to.
         */
        params: { sessionId, unsettled: true, limit: 1, sortOrder: 'asc' },
      })
      .then(({ data }) => {
        if (!cancelled) setCheck(data?.data?.[0] ?? null);
      })
      .catch(() => {
        if (!cancelled) setCheck(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return { check, loading };
}

/** Everything a picker needs in one call: the list, the choice, and its bill. */
export function useVisitPicker(enabled: boolean, branchId?: string | null) {
  const [sessionId, setSessionId] = useState('');
  const { visits, loading: visitsLoading } = useOpenVisits(enabled, branchId);
  const { check, loading: checkLoading } = useVisitCheck(sessionId || null);

  const visit = useMemo(
    () => visits.find((row) => row.id === sessionId) ?? null,
    [visits, sessionId],
  );

  return {
    visits,
    visit,
    sessionId,
    setSessionId,
    check,
    checkId: check?.id ?? null,
    tableId: visit?.tableId ?? null,
    loading: visitsLoading || checkLoading,
    reset: () => setSessionId(''),
  };
}
